import { z } from 'zod'
import { paginationQuerySchema } from './api-response.ts'
import { positiveDecimalSchema, timestampSchema, uuidSchema } from './common.ts'
import { decimalFitsNumeric, warehouseUnitCodeSchema } from './units.ts'

/**
 * Production stages (BRD §4.4).
 *
 * These are SEED DATA, not an enum. The BRD requires stages to be configurable at runtime —
 * add, remove, rename, reorder — so orders reference `stage_templates` rows by id. This list
 * is only what a fresh database starts with, and item 1 of the client's open questions is
 * whether it matches VEEM's real shop.
 */
export const DEFAULT_PRODUCTION_STAGES = [
  { key: 'cutting', label: 'Розкрій', position: 1 },
  { key: 'bending', label: 'Гнуття', position: 2 },
  { key: 'machining', label: 'Механічна обробка', position: 3 },
  { key: 'welding', label: 'Зварювання', position: 4 },
  { key: 'grinding', label: 'Шліфування', position: 5 },
  { key: 'painting', label: 'Фарбування', position: 6 },
  { key: 'qc', label: 'ВТК', position: 7 },
  { key: 'done', label: 'Готово', position: 8 },
] as const

export const stageTemplateSchema = z.object({
  id: z.uuid(),
  key: z.string().min(1),
  label: z.string().min(1),
  position: z.number().int().positive(),
  /** Null when the stage is not machine-bound (e.g. QC). Drives the equipment load panel. */
  equipmentId: z.uuid().nullable(),
  workCenterId: z.uuid().nullable(),
  /**
   * Expected minutes for this stage. Null means "% load is not computable" — see open
   * question 4 for the client: if VEEM cannot supply per-stage durations, equipment load
   * becomes a supervisor-set value instead of a derived one.
   */
  plannedMinutes: z.number().int().positive().nullable(),
  archivedAt: timestampSchema.nullable(),
})

export type StageTemplate = z.infer<typeof stageTemplateSchema>

export const workCenterSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  position: z.number().int().positive(),
  archivedAt: timestampSchema.nullable(),
  createdAt: timestampSchema,
})
export type WorkCenter = z.infer<typeof workCenterSchema>

const workCenterNameSchema = z.string().trim().min(1).max(120)

export const createWorkCenterInputSchema = z.strictObject({ name: workCenterNameSchema })
export type CreateWorkCenterInput = z.infer<typeof createWorkCenterInputSchema>

export const updateWorkCenterInputSchema = z.strictObject({ name: workCenterNameSchema })
export type UpdateWorkCenterInput = z.infer<typeof updateWorkCenterInputSchema>

export const reorderWorkCentersInputSchema = z.strictObject({ ids: z.array(uuidSchema).min(1) })
export type ReorderWorkCentersInput = z.infer<typeof reorderWorkCentersInputSchema>

const stageTemplateFields = z.strictObject({
  key: z
    .string()
    .trim()
    .regex(/^[a-z][a-z0-9_]{0,49}$/),
  label: z.string().trim().min(1).max(100),
  equipmentId: uuidSchema.nullable().default(null),
  workCenterId: uuidSchema.nullable().default(null),
  plannedMinutes: z.number().int().positive().nullable().default(null),
})

export const createStageTemplateInputSchema = stageTemplateFields
export type CreateStageTemplateInput = z.infer<typeof createStageTemplateInputSchema>

export const updateStageTemplateInputSchema = z
  .strictObject({
    label: z.string().trim().min(1).max(100).optional(),
    equipmentId: uuidSchema.nullable().optional(),
    workCenterId: uuidSchema.nullable().optional(),
    plannedMinutes: z.number().int().positive().nullable().optional(),
  })
  .refine((input) => Object.keys(input).length > 0, 'Передайте щонайменше одне поле')
export type UpdateStageTemplateInput = z.infer<typeof updateStageTemplateInputSchema>

export const reorderStageTemplatesInputSchema = z.strictObject({ ids: z.array(uuidSchema).min(1) })
export type ReorderStageTemplatesInput = z.infer<typeof reorderStageTemplatesInputSchema>

export const advanceOrderStageInputSchema = z.strictObject({ stageId: uuidSchema })
export type AdvanceOrderStageInput = z.infer<typeof advanceOrderStageInputSchema>

export const orderStageStatusSchema = z.enum(['pending', 'active', 'done', 'skipped'])

/** Planned production quantities use the same PostgreSQL numeric(18,3) boundary as orders. */
export const productionQuantitySchema = positiveDecimalSchema.refine(
  (value) => decimalFitsNumeric(value, 18, 3),
  'Кількість має відповідати формату numeric(18,3)',
)

export const orderStageSchema = z.object({
  id: uuidSchema,
  orderId: uuidSchema,
  productionOrderItemId: uuidSchema.nullable(),
  templateId: uuidSchema.nullable(),
  key: z.string(),
  label: z.string(),
  position: z.number().int(),
  status: orderStageStatusSchema,
  equipmentId: uuidSchema.nullable(),
  equipmentName: z.string().nullable(),
  workCenterId: uuidSchema.nullable(),
  workCenterName: z.string().nullable(),
  plannedMinutes: z.number().int().nullable(),
  startedAt: timestampSchema.nullable(),
  completedAt: timestampSchema.nullable(),
  completedBy: z.string().nullable(),
})
export type OrderStage = z.infer<typeof orderStageSchema>

const productionOrderItemSnapshotSchema = z.object({
  id: uuidSchema,
  orderId: uuidSchema,
  position: z.number().int().positive(),
  sourceDealLineItemId: uuidSchema.nullable(),
  catalogItemId: uuidSchema.nullable(),
  engineeringProductId: uuidSchema.nullable(),
  engineeringProductName: z.string().nullable(),
  itemKind: z.enum(['product', 'service']).nullable(),
  description: z.string(),
  quantity: z.string().nullable(),
  unit: z.string().nullable(),
  unitSymbol: z.string().nullable(),
  createdAt: timestampSchema,
})

export const productionOrderItemSchema = productionOrderItemSnapshotSchema.extend({
  currentStage: orderStageSchema.nullable(),
  stages: z.array(orderStageSchema),
  completedCount: z.number().int().nonnegative(),
  totalCount: z.number().int().nonnegative(),
  progressPercent: z.number().int().min(0).max(100),
})
export type ProductionOrderItem = z.infer<typeof productionOrderItemSchema>

export const PRODUCTION_PRIORITIES = ['low', 'medium', 'high'] as const
export const productionPrioritySchema = z.enum(PRODUCTION_PRIORITIES)
export type ProductionPriority = z.infer<typeof productionPrioritySchema>

export const updateProductionPriorityInputSchema = z.strictObject({
  priority: productionPrioritySchema,
})
export type UpdateProductionPriorityInput = z.infer<typeof updateProductionPriorityInputSchema>

export const productionOrderSchema = z.object({
  id: uuidSchema,
  number: z.string(),
  productName: z.string(),
  dealId: uuidSchema.nullable(),
  dealName: z.string().nullable(),
  clientName: z.string().nullable(),
  kind: z.enum(['customer', 'internal']),
  plannedQuantity: z.string().nullable(),
  plannedUnit: z.string().nullable(),
  plannedUnitSymbol: z.string().nullable(),
  progressBasis: z.enum(['stages', 'finished_quantity']),
  productionTargetQuantity: z.string().nullable(),
  productionTargetUnit: z.string().nullable(),
  productionTargetUnitSymbol: z.string().nullable(),
  productionTargetUnitPrecision: z.number().int().min(0).max(3).nullable(),
  finishedQuantity: z.string(),
  remainingQuantity: z.string().nullable(),
  requesterType: z.enum(['warehouse', 'work_center']).nullable(),
  requesterName: z.string().nullable(),
  deadline: timestampSchema.nullable(),
  priority: productionPrioritySchema,
  items: z.array(productionOrderItemSchema),
  currentStage: orderStageSchema.nullable(),
  stages: z.array(orderStageSchema),
  completedCount: z.number().int().nonnegative(),
  totalCount: z.number().int().nonnegative(),
  progressPercent: z.number().int().min(0).max(100),
})
export type ProductionOrder = z.infer<typeof productionOrderSchema>

export const productionOrderListQuerySchema = paginationQuerySchema.extend({
  kind: z.enum(['customer', 'internal']).optional(),
  workCenterId: uuidSchema.optional(),
  search: z.string().trim().max(200).optional(),
  sortBy: z.enum(['order', 'stage', 'progress']).optional(),
  sortDirection: z.enum(['ascending', 'descending']).optional(),
})
export type ProductionOrderListQuery = z.infer<typeof productionOrderListQuerySchema>

export const productionReadinessListQuerySchema = z.object({
  orderIds: z
    .string()
    .transform((value) => value.split(',').filter(Boolean))
    .pipe(z.array(uuidSchema).max(100))
    .optional(),
})

export const createInternalProductionOrderInputSchema = z
  .strictObject({
    productName: z.string().trim().min(1).max(500),
    plannedQuantity: productionQuantitySchema,
    unitCode: warehouseUnitCodeSchema,
    deadline: z.iso.date().nullable().default(null),
    requesterType: z.enum(['warehouse', 'work_center']),
    warehouseId: uuidSchema.nullable().default(null),
    workCenterId: uuidSchema.nullable().default(null),
  })
  .superRefine((input, context) => {
    const hasWarehouse = input.warehouseId !== null
    const hasWorkCenter = input.workCenterId !== null
    if (hasWarehouse === hasWorkCenter)
      context.addIssue({
        code: 'custom',
        path: ['warehouseId'],
        message: 'Оберіть один підрозділ-ініціатор',
      })
    if (input.requesterType === 'warehouse' && !hasWarehouse)
      context.addIssue({
        code: 'custom',
        path: ['warehouseId'],
        message: 'Оберіть склад-ініціатор',
      })
    if (input.requesterType === 'work_center' && !hasWorkCenter)
      context.addIssue({
        code: 'custom',
        path: ['workCenterId'],
        message: 'Оберіть дільницю-ініціатора',
      })
  })
export type CreateInternalProductionOrderInput = z.infer<
  typeof createInternalProductionOrderInputSchema
>

export const productionStageSupplyOutcomeSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('not_applicable') }),
  z.object({ kind: z.literal('consumed'), changedMaterialIds: z.array(uuidSchema) }),
  z.object({ kind: z.literal('already_consumed') }),
  z.object({ kind: z.literal('missing_bom'), warning: z.literal('BOM не зафіксовано') }),
])

export type ProductionStageSupplyOutcome = z.infer<typeof productionStageSupplyOutcomeSchema>

export interface ProductionStageAdvanceResult {
  order: ProductionOrder
  advanced: boolean
  completedStage: { id: string; label: string; productionOrderItemId: string | null } | null
  nextStage: { id: string; label: string; productionOrderItemId: string | null } | null
  supplyOutcome: ProductionStageSupplyOutcome
}

/** Engineering project statuses (BRD §4.3). */
export const PROJECT_STATUSES = [
  'sketch',
  'modeling',
  'drawings',
  'bom_approval',
  'review',
  'sent_to_cnc',
] as const

export const projectStatusSchema = z.enum(PROJECT_STATUSES)
export type ProjectStatus = z.infer<typeof projectStatusSchema>

export const PROJECT_STATUS_LABELS: Record<ProjectStatus, string> = {
  sketch: 'Ескіз',
  modeling: 'Моделювання',
  drawings: 'Креслення',
  bom_approval: 'BOM узгодження',
  review: 'Перевірка',
  sent_to_cnc: 'Передано на ЧПУ',
}

/** Equipment states for the load panel (BRD §4.4). */
export const EQUIPMENT_STATUSES = ['running', 'repair', 'idle'] as const

export const equipmentStatusSchema = z.enum(EQUIPMENT_STATUSES)
export type EquipmentStatus = z.infer<typeof equipmentStatusSchema>

export const EQUIPMENT_STATUS_LABELS: Record<EquipmentStatus, string> = {
  running: 'Працює',
  repair: 'В ремонті',
  idle: 'Простій',
}

export const productionEquipmentSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  status: equipmentStatusSchema,
  loadPercent: z.number().int().min(0).max(100).nullable(),
})
export type ProductionEquipment = z.infer<typeof productionEquipmentSchema>

/** The open repair of a machine: why, since when and who took it out of work. */
export const equipmentOpenRepairSchema = z.object({
  id: uuidSchema,
  reason: z.string(),
  startedAt: timestampSchema,
  startedByName: z.string().nullable(),
})
export type EquipmentOpenRepair = z.infer<typeof equipmentOpenRepairSchema>

/** The «Обладнання» list: the machine, the work center whose queue it serves, its open repair. */
export const productionEquipmentDetailSchema = productionEquipmentSchema.extend({
  workCenterId: uuidSchema.nullable(),
  workCenterName: z.string().nullable(),
  repair: equipmentOpenRepairSchema.nullable(),
})
export type ProductionEquipmentDetail = z.infer<typeof productionEquipmentDetailSchema>

const productionEquipmentFields = z.strictObject({
  name: z.string().trim().min(1).max(120),
  status: equipmentStatusSchema,
  loadPercent: z.number().int().min(0).max(100).nullable(),
  // Optional so a client that predates the link keeps working; omitted means "leave as is".
  workCenterId: uuidSchema.nullable().optional(),
})

export const startEquipmentRepairInputSchema = z.strictObject({
  reason: z.string().trim().min(1).max(500),
})
export type StartEquipmentRepairInput = z.infer<typeof startEquipmentRepairInputSchema>

export const createProductionEquipmentInputSchema = productionEquipmentFields
export type CreateProductionEquipmentInput = z.infer<typeof createProductionEquipmentInputSchema>

export const updateProductionEquipmentInputSchema = productionEquipmentFields
  .partial()
  .refine((input) => Object.keys(input).length > 0, 'Передайте щонайменше одне поле')
export type UpdateProductionEquipmentInput = z.infer<typeof updateProductionEquipmentInputSchema>
